export interface PdfSelectableCell {
  ref: string;
  row: string;
  col: string;
  text: string;
  rect: [number, number, number, number];
  table?: number;
}

export interface PdfCellMapPage {
  page: number;
  width: number;
  height: number;
  cells: PdfSelectableCell[];
}

export interface PdfCellReferenceMap {
  version: number;
  source?: string | null;
  mapping_key?: string | null;
  mapping_etag?: string | null;
  mapping_sha256?: string | null;
  source_pdf_key?: string | null;
  source_etag?: string | null;
  pages: PdfCellMapPage[];
}

export const SELECTABLE_CELL_MAP_VERSIONS = new Set([2]);
export const GRID_JSON_CELL_MAP_SOURCES = new Set([
  "current_week_s3",
  "inherited_previous_week",
]);

export interface CellReferenceLike {
  row: string;
  col: string;
}

export interface VersionedCellReferenceLike extends CellReferenceLike {
  cell_map_version?: number | null;
}

interface PdfCellMapCacheIdentity {
  teamId: number;
  teamName: string;
  week: string;
  reportName: string;
  page: number;
  cellMapVersion: number;
  mappingEtag?: string | null;
}

const COLUMN_RE = /^[A-Z]+$/;
const ROW_RE = /^\d+$/;
const ROW_RANGE_RE = /^(\d+)-(\d+)$/;
const FULL_RANGE_RE = /^([A-Z]+)(\d+)-([A-Z]*)(\d+)$/;

export function pdfCellMapCacheKey({
  teamId,
  teamName,
  week,
  reportName,
  page,
  cellMapVersion,
  mappingEtag,
}: PdfCellMapCacheIdentity): string {
  return JSON.stringify([
    teamId,
    teamName.trim(),
    week,
    reportName.trim(),
    page,
    cellMapVersion,
    mappingEtag ?? "",
  ]);
}

export function parsePdfCellReferenceMap(
  value: unknown
): PdfCellReferenceMap | null {
  if (!value) return null;
  try {
    let parsed: unknown = value;
    for (let layer = 0; layer < 2 && typeof parsed === "string"; layer += 1) {
      parsed = JSON.parse(parsed);
    }
    if (!parsed || typeof parsed !== "object") return null;

    const candidate = parsed as Partial<PdfCellReferenceMap>;
    if (!Array.isArray(candidate.pages)) return null;
    if (
      typeof candidate.version !== "number" ||
      !SELECTABLE_CELL_MAP_VERSIONS.has(candidate.version)
    ) {
      return null;
    }
    if (
      !candidate.mapping_key ||
      !candidate.mapping_etag ||
      !candidate.source ||
      !GRID_JSON_CELL_MAP_SOURCES.has(candidate.source)
    ) {
      return null;
    }
    return {
      version: candidate.version,
      source: candidate.source ?? null,
      mapping_key: candidate.mapping_key ?? null,
      mapping_etag: candidate.mapping_etag ?? null,
      mapping_sha256: candidate.mapping_sha256 ?? null,
      source_pdf_key: candidate.source_pdf_key ?? null,
      source_etag: candidate.source_etag ?? null,
      pages: candidate.pages.filter(
        (page): page is PdfCellMapPage =>
          typeof page?.page === "number" &&
          typeof page?.width === "number" &&
          typeof page?.height === "number" &&
          Array.isArray(page?.cells)
      ),
    };
  } catch {
    return null;
  }
}

export function cellReferenceKey(row: string, col: string): string {
  return `${col.trim().toUpperCase()}${row.trim().toUpperCase()}`;
}

export function cellReferencesForMutation(
  references: VersionedCellReferenceLike[],
  fallbackVersion: number
): Array<Required<VersionedCellReferenceLike>> {
  return references.map((reference) => ({
    row: reference.row,
    col: reference.col,
    cell_map_version: reference.cell_map_version ?? fallbackVersion,
  }));
}

function columnNumber(col: string): number | null {
  const normalized = col.trim().toUpperCase();
  if (!COLUMN_RE.test(normalized)) return null;

  let value = 0;
  for (const char of normalized) {
    value = value * 26 + char.charCodeAt(0) - 64;
  }
  return value;
}

function normalizedCellPosition(
  cell: Pick<PdfSelectableCell, "row" | "col">
): { row: number; col: number } | null {
  const row = Number(cell.row);
  const col = columnNumber(cell.col);
  if (!Number.isInteger(row) || row < 1 || col === null) return null;
  return { row, col };
}

export function selectableCellIsInRange(
  cell: PdfSelectableCell,
  start: PdfSelectableCell,
  end: PdfSelectableCell
): boolean {
  if (
    start.table !== undefined &&
    end.table !== undefined &&
    start.table !== end.table
  ) {
    return false;
  }
  if (
    start.table !== undefined &&
    cell.table !== undefined &&
    start.table !== cell.table
  ) {
    return false;
  }

  const cellPosition = normalizedCellPosition(cell);
  const startPosition = normalizedCellPosition(start);
  const endPosition = normalizedCellPosition(end);
  if (!cellPosition || !startPosition || !endPosition) return false;

  const minRow = Math.min(startPosition.row, endPosition.row);
  const maxRow = Math.max(startPosition.row, endPosition.row);
  const minCol = Math.min(startPosition.col, endPosition.col);
  const maxCol = Math.max(startPosition.col, endPosition.col);
  return (
    cellPosition.row >= minRow &&
    cellPosition.row <= maxRow &&
    cellPosition.col >= minCol &&
    cellPosition.col <= maxCol
  );
}

export function buildCellRangeReference(
  start: PdfSelectableCell,
  end: PdfSelectableCell
): CellReferenceLike | null {
  if (
    start.table !== undefined &&
    end.table !== undefined &&
    start.table !== end.table
  ) {
    return null;
  }

  const startPosition = normalizedCellPosition(start);
  const endPosition = normalizedCellPosition(end);
  if (!startPosition || !endPosition) return null;

  const first = startPosition.col <= endPosition.col ? start : end;
  const last = first === start ? end : start;
  const minRow = Math.min(startPosition.row, endPosition.row);
  const maxRow = Math.max(startPosition.row, endPosition.row);
  const startCol = first.col.trim().toUpperCase();
  const endCol = last.col.trim().toUpperCase();

  if (startCol === endCol) {
    return minRow === maxRow
      ? { row: `${minRow}`, col: startCol }
      : { row: `${minRow}-${maxRow}`, col: startCol };
  }

  return {
    row: `${startCol}${minRow}-${endCol}${maxRow}`,
    col: "",
  };
}

export function cellReferenceContainsCell(
  reference: CellReferenceLike,
  cell: Pick<PdfSelectableCell, "row" | "col">
): boolean {
  const cellPosition = normalizedCellPosition(cell);
  if (!cellPosition) return false;

  const row = reference.row.trim().toUpperCase();
  const col = reference.col.trim().toUpperCase();
  if (COLUMN_RE.test(col) && ROW_RE.test(row)) {
    return (
      col === cell.col.trim().toUpperCase() && Number(row) === cellPosition.row
    );
  }

  const sameColumnRange = row.match(ROW_RANGE_RE);
  if (COLUMN_RE.test(col) && sameColumnRange) {
    const startRow = Number(sameColumnRange[1]);
    const endRow = Number(sameColumnRange[2]);
    return (
      col === cell.col.trim().toUpperCase() &&
      cellPosition.row >= Math.min(startRow, endRow) &&
      cellPosition.row <= Math.max(startRow, endRow)
    );
  }

  const fullRange = col === "" ? row.match(FULL_RANGE_RE) : null;
  if (!fullRange) return false;

  const startCol = columnNumber(fullRange[1]);
  const endCol = columnNumber(fullRange[3] || fullRange[1]);
  const startRow = Number(fullRange[2]);
  const endRow = Number(fullRange[4]);
  if (startCol === null || endCol === null) return false;

  return (
    cellPosition.col >= Math.min(startCol, endCol) &&
    cellPosition.col <= Math.max(startCol, endCol) &&
    cellPosition.row >= Math.min(startRow, endRow) &&
    cellPosition.row <= Math.max(startRow, endRow)
  );
}

export function cellReferenceLabel(reference: CellReferenceLike): string {
  const row = reference.row.trim().toUpperCase();
  const col = reference.col.trim().toUpperCase();
  if (!col) return row.replace("-", ":");

  const range = row.match(ROW_RANGE_RE);
  return range ? `${col}${range[1]}:${col}${range[2]}` : `${col}${row}`;
}
